import {Menu as MenuIcon, User as AdminIcon} from "iconoir-react";
import {ComponentType, RefObject, useEffect, useRef, useState} from 'react'
import Button from 'react-bootstrap/Button';
import Nav from 'react-bootstrap/Nav';
import NavDropdown from 'react-bootstrap/NavDropdown';
import Offcanvas from 'react-bootstrap/Offcanvas';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Row from 'react-bootstrap/Row';
import Tooltip from 'react-bootstrap/Tooltip';
import {Placement} from "react-bootstrap/types";
import {useTranslation} from "react-i18next";
import {Outlet, useLocation} from "react-router-dom";
import {LogoutForm} from "../auth/LogoutForm";
import {AccessibleSpinner} from "../common/AccessibleSpinner";
import {LocaleSelector} from "../common/LocaleSelector";
import {useAuthenticatedUserContext} from "../hook/UseAuthenticatedUserContext";
import {useSpinnerStatusContext} from "../hook/UseSpinnerStatusContext";
import './AppLayout.scss'
import '../styles/Styles.scss'

interface AdminLayoutProps {
    AppSidebar : ComponentType<{
        className?: string;
    }>
    Menu? : ComponentType<{
        className?: string;
    }>
}

interface MobileSideBarProps {
    AppSidebar : ComponentType<{
        className?: string;
    }>
}

// mobile sidebar which is show off-canvas
function MobileSideBar({AppSidebar} : Readonly<MobileSideBarProps>)
{
    const [show, setShow] = useState(false);

    const location = useLocation();

    /**
     * Listen for location changes and close the Offcanvas when the location changes
     */
    useEffect(() => {
        setShow(false);
    }, [location]);

    return (
        <>
            <Button variant="primary" className="ms-2" onClick={() => setShow(true)}>
                <MenuIcon height="1em" width="1em"/>
            </Button>

            <Offcanvas show={show} onHide={() => setShow(false)}>
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>CipherMail</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <AppSidebar />
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
}

interface MenuDropdownProps {
    Menu?: ComponentType<{
        className?: string;
    }>,
    logoutSubmitButtonRef: RefObject<HTMLButtonElement>
    tooltipPlacement?: Placement
}

function MenuDropdown({Menu, logoutSubmitButtonRef, tooltipPlacement} : Readonly<MenuDropdownProps>)
{
    const {authenticatedUser, clearAuthenticatedUser} = useAuthenticatedUserContext();
    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    return (
        <OverlayTrigger overlay={<Tooltip>{authenticatedUser}</Tooltip>} placement={tooltipPlacement ?? "right"}>
            <NavDropdown title={<AdminIcon className="cm-app-background-color text-white rounded-circle"/>}>
                {Menu !== undefined && <Menu/>}
                <NavDropdown.Item onClick={() => {
                    clearAuthenticatedUser();
                    logoutSubmitButtonRef.current?.click()
                }}>
                    {t("login.logout")}
                </NavDropdown.Item>
            </NavDropdown>
        </OverlayTrigger>
    )
}

/**
 * Represents the main layout of the authenticated page.
 */
export const AppLayout = ({AppSidebar, Menu} : AdminLayoutProps) =>
{
    const {spinnerStateCounter} = useSpinnerStatusContext();
    /**
     * A reference to the invisible logout submit button which can be used to programmatically submit
     * the logout form when the user selects logout from the menu
     */
    const logoutSubmitButtonRef = useRef<HTMLButtonElement>(null);
    /**
     * i18next translation hook
     */
    const {t} = useTranslation(['translation', 'override']);

    return (
        <div className="container-fluid d-flex flex-column vh-100">
            <Row className="flex-grow-1 min-height-0">
                <div className="sidebar border border-right col-2 d-flex flex-column h-100 d-none d-lg-flex overflow-hidden p-0">
                    <div className="navbar-brand m-2">
                        <img src={t(['override:layout.logo', 'layout.logo'])} width="150" alt="CipherMail Logo"/>
                        <AccessibleSpinner show={spinnerStateCounter > 0} className="ms-3" variant="secondary"/>
                    </div>

                    <div className="sidebar-content flex-fill overflow-y-auto me-2">
                        {<AppSidebar/>}
                    </div>

                    <div className="mt-2">
                        <div className="d-flex border-top border-secondary pt-2">
                            <Nav className="z-index-popover">
                                <MenuDropdown Menu={Menu} logoutSubmitButtonRef={logoutSubmitButtonRef}/>
                            </Nav>
                            <div className="justify-content-end d-flex align-items-center w-100">
                                <LocaleSelector className="bg-transparent text-secondary w-auto me-2"/>
                            </div>
                        </div>
                    </div>
                </div>

                <main className="col-md-12 col-lg-10 d-flex flex-fill flex-column h-100 overflow-auto ps-4 pe-4 pt-2 pb-0">

                    {/*the header will be used to place additional messages on the top of the page*/}
                    <Row id="header"/>

                    <Row className="d-lg-none d-flex">
                        <nav className="navbar navbar-expand">
                            <MobileSideBar AppSidebar={AppSidebar}/>

                            <div className="d-flex justify-content-end w-100 me-2">
                                <MenuDropdown Menu={Menu} logoutSubmitButtonRef={logoutSubmitButtonRef} tooltipPlacement="left"/>
                            </div>
                        </nav>
                    </Row>

                    {/* The child "pages" will be added here*/}
                    <Outlet/>
                </main>
            </Row>

            <LogoutForm ref={logoutSubmitButtonRef}/>
        </div>
    );
}
